/**
 * External File Search Agent Parameter Schema
 * 
 * Searches uploaded external files (PDFs, Excel, etc.) for content.
 * 
 * IMPORTANT: All array and boolean types must have explicit descriptions
 * to help the LLM generate correct JSON types (not stringified values).
 */

import { z } from "zod";
import { SubAgentParams } from "../../../core/types";

// ═══════════════════════════════════════════════════════════════════════════
// SCHEMA DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Requirement item schema
 */
export const RequirementItemSchema = z.object({
    id: z.string().describe("Requirement identifier like 'A', 'B', 'C'"),
    requirement: z.string().describe("Natural language search query describing what to find"),
});

/**
 * Context item schema
 */
export const ContextItemSchema = z.object({
    context: z.array(z.string()).describe("Array of short IDs (can mix types: 'as_abc12', 'd0|slide_5', 'page_xyz')"),
    purpose: z.string().describe("Why these results are relevant to the search"),
});

/**
 * External File Search Parameters Schema
 * 
 * @example
 * {
 *   "requirements": [
 *     { "id": "A", "requirement": "Find revenue projections for next 5 years from financial model" },
 *     { "id": "B", "requirement": "Search for EBITDA margin analysis across all uploaded PDFs" }
 *   ],
 *   "context": [
 *     { "context": ["as_abc12"], "purpose": "AlphaSense analyst estimates - find internal docs that validate" }
 *   ]
 * }
 */
export const ExternalFileSearchParamsSchema = z.object({
    requirements: z.array(RequirementItemSchema)
        .describe("Array of search requirements to fulfill. Must be a JSON array, not a string."),
    uiDescription: z.string()
        .describe("Single-line UI description for top-of-card agent status display."),
    uiDescriptions: z.array(z.string())
        .optional()
        .describe("Optional list of UI description lines for the top section of agent-as-tool cards."),
    
    context: z.array(ContextItemSchema)
        .optional()
        .describe("Optional array of context items with IDs from prior results, AlphaSense data, or slides"),
    
    threadId: z.string()
        .optional()
        .describe("Optional thread ID to continue an existing conversation"),
});

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

/** 
 * External File Search Parameters type (extends SubAgentParams)
 * Use this for type-checking function parameters
 */
export interface ExternalFileSearchParams extends SubAgentParams {}

/** Inferred type from schema for runtime validation */
export type ExternalFileSearchParamsInput = z.infer<typeof ExternalFileSearchParamsSchema>;
